import { useCallback } from 'react';
import { App } from 'antd';

import { useCollectionContext } from '../contexts/CollectionContext';
import { useOptionalFavorites } from '../contexts/FavoritesContext';
import { PIN_KIND } from '../services/favorites';
import { setCollectionOverview } from '../services/overview';
import { normalizeId } from '../utils/ids';

/**
 * Where a chart or a dashboard can be PLACED from its ⋯ (charts A7; plan
 * §4.6; ruled q04, q16, d18) — the props chartActions / dashboardActions
 * take, so Reports, the chart page and the dashboard page offer the same:
 *
 *   chart(chart)          { pinnedHome, onPinHome } — Pin to Home, small at
 *                         first, IN this collection (the context it counts)
 *   dashboard(dashboard)  { pinnedHome, onPinHome } (a launcher), and the
 *                         Overview: { isMyOverview, onShowOnMyOverview,
 *                         onUseCollectionOverview } for anyone, and a
 *                         manager's { isCollectionOverview,
 *                         onShowOnOverviewForEveryone }
 *
 * Whether something is pinned, or is this person's own Overview choice, is
 * the favorites cache's (FavoritesContext); whether the collection shows a
 * dashboard for everyone is the collection document's `overviewDashboard_id`.
 * The server judges every one of these again (a chart pin only where the
 * chart stands for the person here; a choice for everyone only for a
 * dashboard pinned for everyone), and says why in its own words when not.
 */
export default function useChartPlacements(collectionId) {
	const favorites = useOptionalFavorites();
	const context = useCollectionContext() || {};
	const { collection, refreshCollection } = context;
	const { message } = App.useApp();
	const cid = normalizeId(collectionId);

	const chart = useCallback((doc) => {
		if (!favorites || !cid || !doc) return {};
		const id = normalizeId(doc._id);
		return {
			pinnedHome: favorites.isPinned(PIN_KIND.CHART_HOME, id, cid),
			onPinHome: () => favorites.togglePin(PIN_KIND.CHART_HOME, { targetId: id, collectionId: cid }),
		};
	}, [favorites, cid]);

	const showForEveryone = useCallback(async (id) => {
		try {
			await setCollectionOverview(cid, id);
			message.success('Overview shows this dashboard for everyone now.');
			if (typeof refreshCollection === 'function') refreshCollection();
			favorites?.noteOverviewChanged?.();
		} catch (err) {
			message.error(err?.response?.data?.message || 'Could not change the Overview. Please try again.');
		}
	}, [cid, message, refreshCollection, favorites]);

	const dashboard = useCallback((doc) => {
		if (!cid || !doc) return {};
		const id = normalizeId(doc._id);
		const mine = favorites && typeof favorites.overviewChoiceFor === 'function' ? favorites.overviewChoiceFor(cid) : null;
		const shown = collection && normalizeId(collection._id) === cid ? normalizeId(collection.overviewDashboard_id) : null;
		return {
			...(favorites ? {
				pinnedHome: favorites.isPinned(PIN_KIND.DASHBOARD, id, cid),
				onPinHome: () => favorites.togglePin(PIN_KIND.DASHBOARD, { targetId: id, collectionId: cid }),
				isMyOverview: mine === id,
				onShowOnMyOverview: () => favorites.chooseMyOverview(cid, id),
				onUseCollectionOverview: () => favorites.chooseMyOverview(cid, null),
			} : {}),
			isCollectionOverview: Boolean(shown) && shown === id,
			onShowOnOverviewForEveryone: () => showForEveryone(id),
		};
	}, [favorites, cid, collection, showForEveryone]);

	return { chart, dashboard };
}
